import type { ChangeEvent, FC } from 'react';
import { useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import Button from 'epn-ui/umd/dist/components/Button';
import type { FormInstance } from 'epn-ui/umd/dist/components/Form';
import Form from 'epn-ui/umd/dist/components/Form';
import Input from 'epn-ui/umd/dist/components/InputText';
import Select from 'epn-ui/umd/dist/components/Select';

import { faPlus } from '@fortawesome/pro-regular-svg-icons/faPlus';
import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import api from '@api';

import {
    IGetDomainCuttersData,
    IGetOffersByLink,
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
    IResponse,
} from '@api/types';

import BaseSanitizer from '@components/base/BaseSanitizer';
import useI18n from '@i18n';
import { IAffiliateLinksForm } from '@pages/tools/affiliate-links/interface';
import AffiliateCheckboxes from '../AffiliateCheckboxes';

import { subIdRegexp } from '../config';
import { getOfferNames, isValidUrl } from '../utils';

interface IAffiliateForm {
    form: FormInstance<IAffiliateLinksForm>;
    offers: IGetOffersDataFormatted[];
    setOffer: (id: number) => void;
    placements: IGetPlacementsDataFormatted[];
    onSubmit: (data: IAffiliateLinksForm) => Promise<void>;
    loading: boolean;
    setLoading: (flag: boolean) => void;
    isAli: boolean;
    placementError: boolean;
    domainCutters: IGetDomainCuttersData;
    placementsLoading: boolean;
    linkError: boolean;
    setLinkError: (val: boolean) => void;
    requiredPlacement: boolean;
}

type SubId = 1 | 2 | 3 | 4 | 5;

const AffiliateForm: FC<IAffiliateForm> = ({
    form,
    onSubmit,
    offers,
    placements,
    loading,
    setLoading,
    setOffer,
    isAli,
    placementError,
    domainCutters,
    placementsLoading,
    linkError,
    setLinkError,
    requiredPlacement,
}) => {
    const { t } = useI18n('AffiliateLinks');
    const { t: tTools } = useI18n('Tools');
    const { t: tCommon } = useI18n('Common');

    const offersNames = useMemo(() => getOfferNames(offers), [offers]);

    const [subIdFields, setSubIdFields] = useState<{ id: SubId; value: string }[]>([
        { id: 1, value: '' },
    ]);

    const disabledBtn =
        !form.getFieldValue('link') ||
        !form.getFieldValue('offer') ||
        (requiredPlacement ? !form.getFieldValue('placement') : false);

    const SelectOffer = (inputValue, option) =>
        option.name.toLowerCase().includes(inputValue.toLowerCase());
    const SelectPlacement = (inputValue, option) =>
        option.url.toLowerCase().includes(inputValue.toLowerCase());

    const addSubIdField = () => {
        if (subIdFields.length >= 5) return;

        const usedIds = subIdFields.map(f => f.id);
        const freeId = ([1, 2, 3, 4, 5] as SubId[]).find((id: SubId) => !usedIds.includes(id));

        if (!freeId) return;

        setSubIdFields((prev: { id: SubId; value: string }[]) =>
            [...prev, { id: freeId, value: '' }].sort((a, b) => a.id - b.id),
        );
    };

    const removeSubIdField = (id: SubId) => {
        form.setFieldValue(`sub${id}`, undefined);

        setSubIdFields(prev => prev.filter(item => item.id !== id).sort((a, b) => a.id - b.id));
    };

    const setInvalidLinkError = () => {
        form.setFields([
            {
                name: 'link',
                errors: [
                    `${t('The link must belong to the')} ${
                        offersNames[form.getFieldValue('offer')] || ''
                    }`,
                ],
            },
        ]);
        setLinkError(true);
    };

    const validateLink = async (url: string): Promise<void> => {
        if (!url) return;
        if (url.includes('epndev')) return;

        try {
            setLoading(true);
            const resp = (await api.offer.getOfferByLink({
                params: { link: url },
            })) as IResponse<IGetOffersByLink[]>;
            const offer = form.getFieldValue('offer');
            const hasCurrentId = resp.data?.some(({ id }) => id === +offer);

            if (hasCurrentId) {
                form.setFields([{ name: 'link', value: url, errors: [] }]);
                setLinkError(false);
            } else {
                setInvalidLinkError();
            }
        } catch (err) {
            setInvalidLinkError();
        } finally {
            setLoading(false);
        }
    };

    const checkLinkForOffer = debounce(async (url: string): Promise<void> => {
        setLoading(true);

        if (!isValidUrl(url)) {
            form.setFields([
                {
                    name: 'link',

                    errors: [
                        t(
                            'Check that the link is correct. The link must start with https:// or http://',
                        ),
                    ],
                },
            ]);
            setLinkError(true);
            setLoading(false);
            return;
        }

        // выходим если тестируем на инстансе тк ссылка не соответствует ни одному офферу
        if (url.includes('epndev')) {
            setLoading(false);
            return;
        }

        try {
            const { data: offersByLink } = (await api.offer.getOfferByLink({
                params: { link: url },
            })) as IResponse<IGetOffersByLink>;

            // если ссылка принадлежит офферу, то берем id из первого подходящего
            const { id } = offersByLink?.[0];
            form.setFieldValue('offer', id);
            setOffer(id);

            if (id) {
                form.setFields([{ name: 'link', errors: [] }]);
                setLinkError(false);
            } else {
                setInvalidLinkError();
            }
        } catch (err) {
            setInvalidLinkError();
        } finally {
            setLoading(false);
        }
    }, 100);

    const onLinkChanged = (e: ChangeEvent<HTMLInputElement>): void => {
        if (!e.target.value) return;
        checkLinkForOffer(e.target.value);
    };

    return (
        <Form form={form} onFinish={onSubmit}>
            <Form.Item
                name="link"
                label={tTools('Insert your link here')}
                rules={[
                    { required: true, message: t('Please insert your link') },
                    {
                        max: 2000,
                        message: t('Maximum number of characters {{length}}', { length: 2000 }),
                    },
                ]}
            >
                <Input
                    id="AffiliateInputsAffiliateLinkInput"
                    placeholder={tTools('Example: https://aliexpress.ru')}
                    onChange={onLinkChanged}
                />
            </Form.Item>
            <Form.Item
                name="name"
                label={t('Name (Название)')}
                rules={[
                    {
                        max: 100,
                        message: t('Maximum number of characters {{length}}', { length: 100 }),
                    },
                ]}
            >
                <Input
                    id="AffiliateInputsCreativeNameInput"
                    placeholder={tTools('for example: jewelry in footer')}
                />
            </Form.Item>
            <Form.Item name="offer" rules={[{ required: true }]} label={t('Select offer')}>
                <Select
                    id="AffiliateInputsOfferIdSelect"
                    options={offers}
                    onChange={value => {
                        setOffer(value);
                        setLinkError(false);
                        validateLink(form.getFieldValue('link'));
                    }}
                    placeholder={t('All offers')}
                    notFoundContent={tCommon('Nothing found')}
                    filterOption={SelectOffer}
                    showSearch
                    fieldNames={{
                        label: 'name',
                        value: 'id',
                    }}
                />
            </Form.Item>
            <Form.Item
                name="placement"
                rules={[{ required: requiredPlacement }]}
                label={t('Select placement')}
                help={
                    placementError ? (
                        <BaseSanitizer
                            html={t('An placement is required')}
                            options={{ allowedTags: ['a'] }}
                            tag="p"
                            className="text-red-500 text-[12px]"
                        />
                    ) : null
                }
            >
                <Select
                    id="AffiliateInputsPlacementSelect"
                    options={placements}
                    placeholder={
                        placements && placements.length
                            ? t('Select placement')
                            : tCommon('Nothing found')
                    }
                    notFoundContent={tCommon('Nothing found')}
                    fieldNames={{
                        label: 'url',
                        value: 'id',
                    }}
                    disabled={placementError || placementsLoading}
                    filterOption={SelectPlacement}
                    showSearch
                />
            </Form.Item>

            <div className="relative mb-8">
                {subIdFields.map((item, index) => (
                    <Form.Item
                        key={item.id}
                        name={`sub${item.id}`}
                        className="relative mb-6"
                        label={item.id === 1 ? tTools('Insert subid if needed') : ''}
                        rules={[
                            {
                                pattern: subIdRegexp,
                                message: tTools('Sub may only contain the following symbols'),
                            },
                        ]}
                    >
                        <div>
                            <Input
                                id={`AffiliateInputsSub${index}Input`}
                                placeholder={item.id > 1 ? `sub_${item.id}` : 'sub'}
                            />
                            {index !== 0 && (
                                <button
                                    className="absolute flex items-center justify-center w-6 h-6  right-[8px] text-blueGray-300 border-blueGray-300 top-[8px]"
                                    type="button"
                                    onClick={() => removeSubIdField(item.id)}
                                >
                                    <FontAwesomeIcon
                                        className="block text-blueGray-300 text-[16px]"
                                        icon={faTrash}
                                        size="xs"
                                    />
                                </button>
                            )}
                        </div>
                    </Form.Item>
                ))}
                <button
                    className="absolute flex items-center justify-center w-6 h-6  right-[8px] text-blueGray-300 border-blueGray-300 top-[30px]"
                    id="AffiliateInputsAddSubIdButton"
                    type="button"
                    onClick={() => addSubIdField()}
                >
                    <FontAwesomeIcon
                        className="block text-blueGray-300 text-[16px]"
                        icon={faPlus}
                        size="xs"
                    />
                </button>
            </div>
            <div
                className={`flex ${
                    !isAli ? 'justify-between' : 'justify-end'
                } flex-col xs:flex-row`}
            >
                {!isAli && <AffiliateCheckboxes form={form} domainCutters={domainCutters} />}
                <Button
                    id="AffiliateLinksGenerateButton"
                    type="primary"
                    disabled={disabledBtn || loading || linkError}
                    htmlType="submit"
                >
                    {t('Create link')}
                </Button>
            </div>
        </Form>
    );
};

export default AffiliateForm;
